/* eslint-disable max-len */
import { USER_TYPE } from '@/app.config';
import { hasProp } from '@/utils/validate.utils';
import { sortSchedules, getSchedulesBar } from '@/utils/schedules.utils';
import i18n from '@/locales/i18n';

/**
 * Devuelve todas las instalaciones
 *
 * @param {Boolean} ACSMode - Indica si solo tiene que devolver las unidades con ACS
 * @returns {Object} - Listado de instalaciones
 */
const getInstallations = state => ACSMode => {
  if (!ACSMode) return state.installations;

  return Object.keys(state.installations)
    .filter(installationID => {
      return Object.keys(state.installations[installationID].devices).some(mac => {
        const device = state.installations[installationID].devices[mac];
        return device.acs_available;
      });
    })
    .reduce((installations, installationID) => {
      installations[installationID] = state.installations[installationID];
      return installations;
    }, {});
};

/**
 * Devuelve todas las instalaciones en las que el usuario es del tipo avanzado
 *
 * @param {Boolean} ACSMode - Indica si solo tiene que devolver las unidades con ACS
 * @returns {Object} - Listado de instalaciones
 */
const getAdvancedInstallations = state => ACSMode => {
  return Object.keys(state.installations)
    .filter(installationID => {
      const advancedUser = state.installations[installationID].type === USER_TYPE.ADVANCED;

      if (!ACSMode || !advancedUser) return advancedUser;

      return Object.keys(state.installations[installationID].devices).some(mac => {
        const device = state.installations[installationID].devices[mac];
        return device.acs_available;
      });
    })
    .reduce((installations, installationID) => {
      installations[installationID] = state.installations[installationID];
      return installations;
    }, {});
};

/**
 * Devuelve todos los datos de una instalación
 *
 * @param {String} installationID - ID de la instalación
 * @returns {Installation} - Datos de una instalación
 */
const getInstallation = state => installationID => {
  return state.installations && state.installations[installationID]
    ? state.installations[installationID]
    : null;
};

/**
 * Devuelve todos los dispositivos de una instalación
 *
 * @param {String} installationID - Id de la instalación
 * @param {Boolean} ACSMode - Indica si solo tiene que devolver las unidades con ACS
 * @returns {Object} - Dispositivos de una instalación
 */
const getDevices = state => installationID => {
  return state.installations[installationID].devices;
};

/**
 * Devuelve todos los dispositivos con ACS de una instalación
 *
 * @param {String} installationID - Id de la instalación
 * @returns {Object} - Dispositivos de una instalación
 */
const getACSDevices = state => installationID => {
  return Object.keys(state.installations[installationID].devices)
    .filter(mac => {
      const device = state.installations[installationID].devices[mac];
      return device.acs_available;
    })
    .reduce((devices, mac) => {
      devices[mac] = state.installations[installationID].devices[mac];
      return devices;
    }, {});
};

/**
 * Devuelve todos los dispositivos de Aire de una instalación
 *
 * @param {String} installationID - Id de la instalación
 * @returns {Object} - Dispositivos de una instalación
 */
const getAirDevices = state => installationID => {
  return Object.keys(state.installations[installationID].devices)
    .filter(mac => {
      const device = state.installations[installationID].devices[mac];
      return !device.acs_available || (device.acs_available && device.tai_th);
    })
    .reduce((devices, mac) => {
      devices[mac] = state.installations[installationID].devices[mac];
      return devices;
    }, {});
};

/**
 * Devuelve todos los datos de un dispositivo
 *
 * @param {String} installationID - ID de la instalación
 * @param {String} deviceMAC - Mac del dispositivo
 * @returns {Device} - Datos de un dispositivo
 */
const getDevice = state => (installationID, deviceMAC) => {
  return state.installations &&
    state.installations[installationID] &&
    state.installations[installationID].devices[deviceMAC]
    ? state.installations[installationID].devices[deviceMAC]
    : null;
};

/**
 * Devuelve la instalación a la que pertenece un dispositivo
 *
 * @param {String} deviceMac - Mac del dispositivo
 * @returns {Installation} - Datos de la instalación
 */
const getInstallationByDevice = state => deviceMac => {
  const installation = Object.keys(state.installations).find(installationID => {
    return Object.keys(state.installations[installationID].devices).find(mac => deviceMac === mac);
  });

  if (installation) return state.installations[installation];
  return null;
};

/**
 * Devuelve la instalación y el dispositivo al que pertenece un dispositivo de terceros
 *
 * @param {String} thirdPartyId - Id del dispositivo de terceros
 * @returns {Object} - Datos de la instalación y el dispositivo
 */
const getUnitByThirdPartyDevice = state => thirdPartyId => {
  let data = {};

  Object.keys(state.installations).find(installationID => {
    return Object.keys(state.installations[installationID].devices).find(deviceMac => {
      if (
        hasProp(state.installations[installationID].devices[deviceMac], 'link_deviceID') &&
        state.installations[installationID].devices[deviceMac].link_deviceID === thirdPartyId
      ) {
        data = {
          installationID,
          deviceMac,
          installation: state.installations[installationID].name,
          unit: state.installations[installationID].devices[deviceMac].name,
        };
        return true;
      }
      return false;
    });
  });

  return data;
};

const getSchedules = state => installationID => {
  const installation = state.installations[installationID];
  const installationSchedules = {};

  if (!hasProp(installation, 'schedules')) return installationSchedules;

  // Recorro todas las programaciones de una instalación
  Object.keys(installation.schedules).forEach(scheduleID => {
    const schedule = installation.schedules[scheduleID];
    // Recorro todos los dispositivos de una programación
    schedule.devices.forEach(mac => {
      // Compruebo si ya he añadido ese dispositivo
      if (!hasProp(installationSchedules, mac)) {
        installationSchedules[mac] = {
          schedules: { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] },
          schedulesACS: { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] },
        };
      }

      // Recorro todos los días de una programación y añado la programación
      schedule.days.forEach(day => {
        if (schedule.acs) {
          installationSchedules[mac].schedulesACS[day].push(schedule);
        } else {
          installationSchedules[mac].schedules[day].push(schedule);
        }
      });
    });
  });

  // Ordena las programaciones de cada día por su hora de inicio
  Object.keys(installationSchedules).forEach(mac => {
    Object.keys(installationSchedules[mac].schedules).forEach(day => {
      installationSchedules[mac].schedules[day].sort(sortSchedules);
    });
    Object.keys(installationSchedules[mac].schedules).forEach(day => {
      installationSchedules[mac].schedulesACS[day].sort(sortSchedules);
    });

    // Genero las barras de las programaciones
    installationSchedules[mac].schedulesBar = getSchedulesBar(installationSchedules[mac].schedules);
    installationSchedules[mac].schedulesBarACS = getSchedulesBar(
      installationSchedules[mac].schedulesACS
    );
  });

  return installationSchedules;
};

const getNextSchedule = (state, getters) => (installationID, mac, ACSMode) => {
  // Obtengo las programaciones
  const installationSchedules = getters.getSchedules(installationID);

  if (!installationSchedules[mac]) return null;

  const deviceSchedules = ACSMode
    ? installationSchedules[mac].schedulesACS
    : installationSchedules[mac].schedules;

  // Obtengo el día y hora actual
  const date = new Date();
  const hour = date.getHours();
  const minutes = date.getMinutes();
  const today = date.getDay();

  // Ordeno los días de la semana para que empiecen por el día actual
  const days = [0, 1, 2, 3, 4, 5, 6];
  const sortedDays = days.slice(today).concat(days.slice(0, today));

  // Inicializo los valores
  let scheduleDay = 0;
  let nextSchedule = null;

  // Empiezo comprobando si hay programaciones en un día, y si las hay, guardo la primera,
  // al empezar a buscar por el día actual, tengo que comprobar si hay alguna programación después
  // de la hora actual, en cualquier otro caso, será la primera programación que encuentre
  for (let i = 0; i < 7; i += 1) {
    const day = sortedDays[i];
    const schedulesByDay = deviceSchedules[day];

    if (schedulesByDay && schedulesByDay.length) {
      if (day === today) {
        scheduleDay = day;
        nextSchedule = schedulesByDay.find(scheduleItem => {
          if (!scheduleItem.enabled) return false;
          return (
            scheduleItem.hour > hour ||
            (scheduleItem.hour === hour && scheduleItem.minutes > minutes)
          );
        });

        if (nextSchedule) break;
        else nextSchedule = schedulesByDay.find(scheduleItem => scheduleItem.enabled);
      } else {
        const scheduleIndex = schedulesByDay.findIndex(scheduleItem => scheduleItem.enabled);
        if (scheduleIndex >= 0) {
          scheduleDay = day;
          nextSchedule = schedulesByDay[scheduleIndex];
          break;
        }
      }
    }
  }

  // Si he encontrado alguna programación devuelvo el día en formato String
  // En la vista se traducirá al idioma correspondiente
  if (nextSchedule) {
    const daysString = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
    nextSchedule.dayString = daysString[scheduleDay];
  }

  return nextSchedule;
};

const getScene = state => (installationID, sceneID) => {
  const scenes = state.installations[installationID].scenes;

  return hasProp(scenes, sceneID) ? scenes[sceneID] : null;
};

const getScenesOrder = state => installationID => {
  const installation = state.installations[installationID];

  return hasProp(installation, 'scenesOrder') ? installation.scenesOrder : null;
};

const getDevicesError = state => {
  const devicesError = [];

  Object.keys(state.installations).forEach(installationID => {
    Object.keys(state.installations[installationID].devices).forEach(deviceID => {
      const device = state.installations[installationID].devices[deviceID];
      let error = '';

      if (!hasProp(device, 'isConnected')) return;

      if (!device.isConnected) {
        error = i18n.t('home.notConnected');
      } else if (device.isConnected && !device.machineready && !device.error_value) {
        error = i18n.t('home.machineNotReady');
      } else if (device.isConnected && device.error_value) {
        const errorCode = device.error_ascii2
          ? `${device.error_ascii1}${device.error_ascii2}`
          : device.error_ascii1;
        error = `${i18n.t('new.unit.machineError')}: ${errorCode}`;
      } else if (device.tsensor_error) {
        error = i18n.t('unit.sensor');
      }

      if (error) {
        devicesError.push({ name: device.name, description: error });
      }
    });
  });

  return devicesError;
};

export default {
  getInstallations,
  getAdvancedInstallations,
  getInstallation,
  getDevices,
  getACSDevices,
  getAirDevices,
  getDevice,
  getInstallationByDevice,
  getUnitByThirdPartyDevice,
  getSchedules,
  getNextSchedule,
  getScene,
  getScenesOrder,
  getDevicesError,
};
